import React, { memo, useMemo } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { adcoMethodLabel, isAdcoGeneratedVector } from '../utils/adcoDisplayUtils';
import { getCurveScenarioLabel } from '../utils/curveScenarioUtils';

const SOURCE_CONFIG = {
  base: {
    label: 'Baseline',
    color: 'primary',
    icon: LockOutlinedIcon,
  },
  adco: {
    label: 'ADCO model',
    color: 'info',
    icon: AutoGraphOutlinedIcon,
  },
  custom: {
    label: 'Custom',
    color: 'secondary',
    icon: TuneOutlinedIcon,
  },
};

const hasTargetValue = (target) => (
  (target?.price !== null && target?.price !== undefined)
  || (target?.yield !== null && target?.yield !== undefined)
  || (target?.spread !== null && target?.spread !== undefined)
);

const describeTarget = (tranche, target) => {
  if (target?.price !== null && target?.price !== undefined) {
    return `${tranche} · Price ${target.price}`;
  }
  if (target?.yield !== null && target?.yield !== undefined) {
    return `${tranche} · Yield ${target.yield}%`;
  }
  return `${tranche} · Spread ${target?.spread} bps`;
};

const ComparisonRunCard = memo(({
  run,
  index,
  dealData,
  locked = false,
  onEdit,
  onRemove,
  curveScenarioOptions = [],
}) => {
  const theme = useTheme();
  const source = SOURCE_CONFIG[run?.source] || SOURCE_CONFIG.custom;
  const SourceIcon = source.icon;
  const curveScenarioLabel = getCurveScenarioLabel(run?.curve_scenario, curveScenarioOptions);

  const details = useMemo(() => {
    const overrideDetails = Object.entries(run?.deal_overrides || {}).map(([key, value]) => {
      const defaultValue = dealData?.[key] ?? 'Default';
      const isGeneratedVector = isAdcoGeneratedVector(key, defaultValue, dealData);
      return {
        key: `override-${key}`,
        label: `${key}: ${isGeneratedVector ? 'ADCO Generated' : defaultValue} → ${value}`,
        tooltip: isGeneratedVector ? defaultValue : null,
        tone: 'warning',
      };
    });
    const targetDetails = Object.entries(run?.tranche_target || {})
      .filter(([, target]) => hasTargetValue(target))
      .map(([tranche, target]) => ({
        key: `target-${tranche}`,
        label: describeTarget(tranche, target),
        tone: 'info',
      }));
    const shockDetails = run?.shock_bps ? [{
      key: 'shock-bps',
      label: `Shock: ${run.shock_bps > 0 ? '+' : ''}${run.shock_bps}bps`,
      tooltip: `Parallel ${run.shock_bps > 0 ? 'up' : 'down'} shift of ${Math.abs(run.shock_bps)}bps`,
      tone: 'info',
    }] : [];
    const curveDetails = run?.curve_scenario ? [{
      key: 'curve-scenario',
      label: `Curve: ${curveScenarioLabel}`,
      tooltip: run.curve_asofdt
        ? `${curveScenarioLabel} as of ${run.curve_asofdt}`
        : curveScenarioLabel,
      tone: 'info',
    }] : [];
    const methodDetails = run?.source === 'adco' ? [{
      key: 'adco-method',
      label: adcoMethodLabel(run.adco_cshf),
      tooltip: run.adco_cshf
        ? 'CPR/CDR/severity derived from the loans\' summed ADCO cashflow dollars'
        : 'CPR/CDR/severity averaged from ADCO\'s own loan rates',
      tone: 'info',
    }] : [];
    return [...shockDetails, ...curveDetails, ...methodDetails, ...overrideDetails, ...targetDetails];
  }, [curveScenarioLabel, dealData, run]);

  const summary = useMemo(() => {
    const trancheCount = Object.keys(run?.tranche_target || {}).length;
    if (run?.source === 'base') {
      return `${trancheCount} selected tranche${trancheCount === 1 ? '' : 's'} · Current assumptions`;
    }
    if (run?.source === 'adco') {
      const curveOrShock = run?.shock_bps
        ? `${run.shock_bps > 0 ? '+' : ''}${run.shock_bps}bps`
        : curveScenarioLabel;
      return `${trancheCount} tranche${trancheCount === 1 ? '' : 's'} · ${curveOrShock}`;
    }
    const overrideCount = Object.keys(run?.deal_overrides || {}).length;
    const targetCount = Object.values(run?.tranche_target || {}).filter(hasTargetValue).length;
    return `${overrideCount} assumption override${overrideCount === 1 ? '' : 's'} · ${targetCount} pricing target${targetCount === 1 ? '' : 's'}`;
  }, [curveScenarioLabel, run]);

  return (
    <Paper
      data-testid="comparison-run-card"
      variant="outlined"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: 1.5,
        borderRadius: 2.5,
        borderColor: alpha(theme.palette[source.color].main, 0.28),
        bgcolor: alpha(theme.palette[source.color].main, theme.palette.mode === 'dark' ? 0.09 : 0.035),
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '0 auto 0 0',
          width: 4,
          bgcolor: theme.palette[source.color].main,
        },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            color: theme.palette[source.color].main,
            bgcolor: alpha(theme.palette[source.color].main, 0.12),
            flexShrink: 0,
          }}
        >
          <SourceIcon sx={{ fontSize: 19 }} />
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
            <Typography
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 700,
                fontSize: '13px',
                color: 'text.primary',
              }}
            >
              {run?.label || `Run ${index + 1}`}
            </Typography>
            <Chip
              label={source.label}
              size="small"
              color={source.color}
              variant="outlined"
              sx={{ height: 20, fontSize: '10px', fontWeight: 700 }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            {summary}
          </Typography>
        </Box>

        {!locked && (
          <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
            {onEdit && (
              <Tooltip title={`Edit ${run.label}`}>
                <IconButton
                  size="small"
                  aria-label={`Edit ${run.label}`}
                  onClick={() => onEdit(index)}
                >
                  <EditOutlinedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
            {onRemove && (
              <Tooltip title={`Remove ${run.label}`}>
                <IconButton
                  size="small"
                  aria-label={`Remove ${run.label}`}
                  color="error"
                  onClick={() => onRemove(index)}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>

      {details.length > 0 && (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1.25, pl: 5.75 }}>
          {details.slice(0, 4).map(detail => (
            <Tooltip key={detail.key} title={detail.tooltip || detail.label}>
              <Chip
                label={detail.label}
                size="small"
                sx={{
                  maxWidth: 210,
                  height: 22,
                  fontSize: '10px',
                  bgcolor: alpha(theme.palette[detail.tone].main, 0.1),
                  color: theme.palette[detail.tone].dark,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            </Tooltip>
          ))}
          {details.length > 4 && (
            <Chip
              label={`+${details.length - 4} more`}
              size="small"
              variant="outlined"
              sx={{ height: 22, fontSize: '10px' }}
            />
          )}
        </Stack>
      )}
    </Paper>
  );
});

ComparisonRunCard.displayName = 'ComparisonRunCard';

export default ComparisonRunCard;
